import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { courseAPI } from '../lib/api';
import { Play, FileText, Loader2, CheckCircle2, BookOpen, ChevronRight } from 'lucide-react';

export default function CoursePreviewPage() {
  const { courseId } = useParams();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeModule, setActiveModule] = useState(0);
  const [activeLesson, setActiveLesson] = useState(0);
  const [completed, setCompleted] = useState([]);

  useEffect(() => {
    courseAPI.get(courseId).then(r => { setCourse(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [courseId]);

  const getEmbedUrl = (url) => {
    if (!url) return '';
    const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s]+)/);
    if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
    const vim = url.match(/vimeo\.com\/(\d+)/);
    if (vim) return `https://player.vimeo.com/video/${vim[1]}`;
    return url;
  };

  if (loading) return <div className="flex items-center justify-center min-h-screen bg-[#F8F9FA]"><Loader2 className="h-8 w-8 animate-spin" style={{ color: '#195259' }} /></div>;
  if (!course) return <div className="flex items-center justify-center min-h-screen bg-[#F8F9FA]"><p className="text-muted-foreground">Course not found</p></div>;

  const modules = course.modules || [];
  const currentMod = modules[activeModule];
  const currentLesson = currentMod?.lessons?.[activeLesson];
  const totalLessons = modules.reduce((s, m) => s + m.lessons.length, 0);
  const progressPct = totalLessons > 0 ? Math.round((completed.length / totalLessons) * 100) : 0;

  const toggleComplete = (lessonId) => {
    setCompleted(prev => prev.includes(lessonId) ? prev.filter(id => id !== lessonId) : [...prev, lessonId]);
  };

  return (
    <div className="flex min-h-screen" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Sidebar */}
      <div className="w-80 shrink-0 border-r bg-white flex flex-col" style={{ maxHeight: '100vh', position: 'sticky', top: 0 }}>
        {/* Course header */}
        <div className="p-5 border-b" style={{ backgroundColor: '#195259' }}>
          <h1 className="text-white font-semibold text-sm leading-tight">{course.title}</h1>
          <div className="flex items-center gap-2 mt-2">
            <div className="flex-1 h-1.5 rounded-full bg-white/20 overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${progressPct}%`, backgroundColor: '#FF9A00' }} />
            </div>
            <span className="text-white/70 text-[10px]">{progressPct}%</span>
          </div>
          <p className="text-white/50 text-[10px] mt-1">{completed.length}/{totalLessons} lessons completed</p>
        </div>

        {/* Module list */}
        <div className="flex-1 overflow-y-auto p-3 space-y-1">
          {modules.map((mod, mi) => (
            <div key={mod.id}>
              <button onClick={() => { setActiveModule(mi); if (mod.lessons.length) setActiveLesson(0); }}
                className={`w-full text-left px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${activeModule === mi ? 'bg-[#195259]/10 text-[#195259]' : 'text-gray-600 hover:bg-gray-50'}`}>
                <span className="text-[10px] text-gray-400 mr-1.5">{mi + 1}.</span> {mod.title}
              </button>
              {mod.lessons.map((les, li) => {
                const isActive = activeModule === mi && activeLesson === li;
                const isDone = completed.includes(les.id);
                return (
                  <button key={les.id} onClick={() => { setActiveModule(mi); setActiveLesson(li); }}
                    className={`w-full text-left flex items-center gap-2 px-3 py-2 ml-3 rounded-lg text-xs transition-colors ${isActive ? 'bg-[#FF9A00]/10 text-[#195259] font-medium' : 'text-gray-500 hover:bg-gray-50'}`}>
                    {isDone ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" /> : les.video_url ? <Play className="h-3.5 w-3.5 shrink-0" /> : <FileText className="h-3.5 w-3.5 shrink-0" />}
                    <span className="truncate">{les.title}</span>
                    {les.duration_mins > 0 && <span className="text-[9px] text-gray-400 shrink-0 ml-auto">{les.duration_mins}m</span>}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 bg-[#F8F9FA]">
        {currentLesson ? (
          <div>
            {/* Video */}
            {currentLesson.video_url && (
              <div className="bg-black">
                <div className="max-w-4xl mx-auto aspect-video">
                  <iframe src={getEmbedUrl(currentLesson.video_url)} className="w-full h-full" allowFullScreen title={currentLesson.title} style={{ border: 'none' }} />
                </div>
              </div>
            )}

            {/* Lesson content */}
            <div className="max-w-3xl mx-auto px-8 py-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider">{currentMod?.title} &middot; Lesson {activeLesson + 1}</p>
                  <h2 className="text-xl font-semibold mt-1" style={{ color: '#195259' }}>{currentLesson.title}</h2>
                </div>
                <button onClick={() => toggleComplete(currentLesson.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${completed.includes(currentLesson.id) ? 'bg-emerald-100 text-emerald-700' : 'bg-white border text-gray-600 hover:border-[#195259]'}`}
                  data-testid="mark-complete-btn">
                  <CheckCircle2 className="h-4 w-4" />
                  {completed.includes(currentLesson.id) ? 'Completed' : 'Mark as Complete'}
                </button>
              </div>

              {currentLesson.content && (
                <div className="prose prose-sm max-w-none text-gray-600 whitespace-pre-wrap leading-relaxed">
                  {currentLesson.content}
                </div>
              )}

              {currentLesson.file_url && (
                <a href={currentLesson.file_url} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-6 px-4 py-2.5 rounded-xl border bg-white text-sm font-medium text-gray-700 hover:border-[#195259] transition-colors">
                  <FileText className="h-4 w-4" /> {currentLesson.file_name || 'Download Attachment'}
                </a>
              )}

              {/* Next lesson */}
              {(activeLesson < (currentMod?.lessons?.length || 0) - 1 || activeModule < modules.length - 1) && (
                <button onClick={() => {
                  if (activeLesson < (currentMod?.lessons?.length || 0) - 1) {
                    setActiveLesson(activeLesson + 1);
                  } else if (activeModule < modules.length - 1) {
                    setActiveModule(activeModule + 1);
                    setActiveLesson(0);
                  }
                }} className="mt-8 flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90" style={{ backgroundColor: '#195259' }} data-testid="next-lesson-btn">
                  Next Lesson <ChevronRight className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <BookOpen className="h-12 w-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-400">Select a lesson to begin</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
